import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, ChapterLabel } from "@/components/Reveal";
import { useLang } from "@/i18n";

const Testimonial = () => {
  const { t } = useLang();
  const d = t.testimonial;
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const reduceMotion = useReducedMotion();
  const items = d.items ?? [];
  const hasMultiple = items.length > 1;

  useEffect(() => {
    if (!hasMultiple || hasInteracted || reduceMotion) return undefined;
    const timer = window.setInterval(() => setActiveIndex((current) => (current + 1) % items.length), 14000);
    return () => window.clearInterval(timer);
  }, [items.length, hasMultiple, hasInteracted, reduceMotion]);

  useEffect(() => setActiveIndex(0), [d.items]);

  // The section remains hidden until approved bilingual testimonials are present.
  if (!items.length) return null;

  const testimonial = items[activeIndex] ?? items[0];

  const showPrevious = () => { setHasInteracted(true); setActiveIndex((current) => (current - 1 + items.length) % items.length); };
  const showNext = () => { setHasInteracted(true); setActiveIndex((current) => (current + 1) % items.length); };

  return (
    <section data-testid="testimonial-section" className="overflow-hidden bg-ivory px-6 py-28 md:px-12 md:py-44" onPointerEnter={() => setHasInteracted(true)} onPointerDown={() => setHasInteracted(true)} onFocusCapture={() => setHasInteracted(true)}>
      <div className="mx-auto max-w-4xl text-left md:text-center">
        <div className="md:flex md:justify-center">
          <ChapterLabel index={d.chapter} title={d.label} />
        </div>
        <Reveal delay={0.18}>
          <AnimatePresence mode="wait">
            <motion.div key={testimonial.id} initial={reduceMotion ? false : { opacity: 0, x: 28 }} animate={{ opacity: 1, x: 0 }} exit={reduceMotion ? {} : { opacity: 0, x: -28 }} transition={{ duration: reduceMotion ? 0 : 0.55 }}>
              <blockquote data-testid="testimonial-quote" className="mt-12 font-display text-3xl font-light italic leading-[1.2] tracking-tight text-charcoal sm:text-4xl lg:text-[3.2rem]">
                {testimonial.quote}
              </blockquote>
              <div className="mt-10 flex items-center gap-4 md:justify-center">
                <p className="label-caps text-charcoal/55">{testimonial.author}</p>
                <span className="h-px w-8 bg-gold/70" aria-hidden="true" />
                <p className="label-caps text-charcoal/40">{testimonial.source}</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </Reveal>
        {hasMultiple && <div className="mt-10 flex items-center gap-5 md:justify-center" aria-label={d.controls.controls}>
          <button type="button" onClick={showPrevious} className="p-2 text-charcoal/60 transition-colors hover:text-charcoal" aria-label={d.controls.previous}><ChevronLeft size={20} strokeWidth={1.25} /></button>
          <p className="label-caps text-charcoal/40">{String(activeIndex + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}</p>
          <button type="button" onClick={showNext} className="p-2 text-charcoal/60 transition-colors hover:text-charcoal" aria-label={d.controls.next}><ChevronRight size={20} strokeWidth={1.25} /></button>
        </div>}
      </div>
    </section>
  );
};

export default Testimonial;
